(() => {
  const SUBMITTED_KEY = 'ab10_submitted';
  const DISMISSED_KEY = 'ab10_dismissed';

  const root = document.querySelector('[data-ab10-root]');
  if (!root) return;

  const overlay = root.querySelector('[data-ab10-overlay]');
  const modal = root.querySelector('[data-ab10-modal]');
  const closeBtn = root.querySelector('[data-ab10-close]');
  const noThanksBtn = root.querySelector('[data-ab10-no-thanks]');
  const widget = root.querySelector('[data-ab10-widget]');
  const form =
    root.querySelector('[data-ab10-form]') ||
    root.querySelector('#ab10EmailCapture');
  const errorEl = root.querySelector('[data-ab10-error]');
  const toastEl = root.querySelector('[data-ab10-toast]');

  if (!overlay || !modal || !widget || !form) return;

  const rootUrlRaw = (root.getAttribute('data-ab10-root-url') || '').trim();
  const rootUrl = rootUrlRaw === '/' ? '' : rootUrlRaw.replace(/\/+$/, '');
  const discountCode = (root.getAttribute('data-ab10-discount-code') || '').trim();
  const widgetSide = (root.getAttribute('data-ab10-widget-side') || 'right').trim();
  const acceptsMarketing = root.getAttribute('data-ab10-accepts-marketing') === '1';

  if (widgetSide === 'left') widget.classList.add('ab10__widget--left');

  const isSubmitted = () => window.localStorage.getItem(SUBMITTED_KEY) === '1';
  const isDismissed = () => window.localStorage.getItem(DISMISSED_KEY) === '1';

  const showWidget = () => {
    if (isSubmitted()) return;
    widget.hidden = false;
  };

  const hideWidget = () => {
    widget.hidden = true;
  };

  const openModal = () => {
    if (isSubmitted()) return;
    overlay.hidden = false;
    modal.hidden = false;
    document.documentElement.classList.add('ab10--noScroll');
    const email = form.querySelector('input[name="contact[email]"]');
    if (email) setTimeout(() => email.focus(), 0);
  };

  const closeModal = () => {
    overlay.hidden = true;
    modal.hidden = true;
    document.documentElement.classList.remove('ab10--noScroll');
  };

  const dismiss = () => {
    try {
      window.localStorage.setItem(DISMISSED_KEY, '1');
    } catch (_) {}
    closeModal();
    showWidget();
  };

  const setError = (msg) => {
    if (!errorEl) return;
    if (!msg) {
      errorEl.hidden = true;
      errorEl.textContent = '';
      return;
    }
    errorEl.textContent = msg;
    errorEl.hidden = false;
  };

  let toastTimer;
  const showToast = (msg) => {
    if (!toastEl) return;
    toastEl.textContent = msg;
    toastEl.hidden = false;
    window.clearTimeout(toastTimer);
    toastTimer = window.setTimeout(() => {
      toastEl.hidden = true;
      toastEl.textContent = '';
    }, 3500);
  };

  const markSubmitted = () => {
    try {
      window.localStorage.setItem(SUBMITTED_KEY, '1');
      window.localStorage.removeItem(DISMISSED_KEY);
    } catch (_) {}
  };

  const applyDiscountWithCartAjax = async () => {
    if (!discountCode) return { ok: true, reason: 'no_code' };

    const res = await fetch(`${rootUrl}/cart/update.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      credentials: 'same-origin',
      body: JSON.stringify({ discount: discountCode }),
    });

    const data = await res.json().catch(() => null);
    if (!res.ok) {
      return { ok: false, reason: 'request_failed', status: res.status, data };
    }

    return { ok: true, data };
  };

  const stripAb10Param = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete('ab10');
    window.history.replaceState(
      {},
      '',
      url.pathname + (url.searchParams.toString() ? `?${url.searchParams.toString()}` : '') + url.hash,
    );
  };

  const handlePostSignupReturn = async () => {
    const url = new URL(window.location.href);
    const flag = url.searchParams.get('ab10');
    if (flag !== 'pending') return;

    markSubmitted();
    hideWidget();
    closeModal();

    const applied = await applyDiscountWithCartAjax();

    if (applied.ok) {
      showToast('Success! Your 10% off has been applied.');
    } else {
      showToast('You’re signed up! If your discount doesn’t show at checkout, refresh and try again.');
    }

    stripAb10Param();

    if (window.location.pathname.includes('/cart')) {
      window.setTimeout(() => window.location.reload(), applied.ok ? 250 : 0);
    }
  };

  const ensureReturnTo = () => {
    // After Shopify form submission, come back to this page with a flag.
    // Do NOT route through /discount/... in return_to — that path often triggers Shopify challenges.
    const url = new URL(window.location.href);
    url.searchParams.set('ab10', 'pending');
    const redirect = url.pathname + (url.searchParams.toString() ? `?${url.searchParams.toString()}` : '') + url.hash;
    const returnTo = `${rootUrl}${redirect}`;

    let returnToInput = form.querySelector('input[name="return_to"]');
    if (!returnToInput) {
      returnToInput = document.createElement('input');
      returnToInput.type = 'hidden';
      returnToInput.name = 'return_to';
      form.appendChild(returnToInput);
    }
    returnToInput.value = returnTo;
  };

  // Initial state
  handlePostSignupReturn();
  if (!isSubmitted() && !isDismissed()) {
    // First hit on any page
    openModal();
  } else if (!isSubmitted() && isDismissed()) {
    showWidget();
  }

  overlay.addEventListener('click', dismiss);
  closeBtn?.addEventListener('click', dismiss);
  noThanksBtn?.addEventListener('click', dismiss);

  widget.addEventListener('click', () => {
    setError('');
    openModal();
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && !modal.hidden) dismiss();
  });

  form.addEventListener('submit', (e) => {
    setError('');
    ensureReturnTo();

    const emailInput = form.querySelector('input[name="contact[email]"]');
    const email = (emailInput?.value || '').trim();
    const looksValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

    if (!looksValid) {
      e.preventDefault();
      setError('Please enter a valid email address.');
      emailInput?.focus();
      return;
    }

    const submitBtn = root.querySelector('[data-ab10-submit]');
    submitBtn?.setAttribute('disabled', 'disabled');
    submitBtn?.classList.add('ab10__submit--loading');

    // Let Shopify submit the form normally (most reliable; avoids AJAX-triggered challenges).
    // If Shopify redirects back with ?ab10=success, we’ll mark submitted + toast.
    try {
      window.sessionStorage.setItem('ab10_pending_success', '1');
    } catch (_) {}
  });
})();

